import useUpdateMaterialsListMaterials from '@/api/mutations/materialsList/useUpdateMaterialsListMaterials';
import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import { getMaterials } from '@/utils/stateAccessors';
import { Button } from '@payaca/ambrosia';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';

import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import {
  useDeal,
  useMaterialsList,
  useMaterialsListMaterialsForMaterialsList,
} from '@payaca/store/hooks/appState';
import * as materialsListActions from '@payaca/store/materialsList/materialsListActions';
import { MaterialsListMaterial } from '@payaca/types/materialsListTypes';
import { useQueryClient } from '@tanstack/react-query';
import {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import BuildMaterialsListSidebar from '../buildMaterialsListSidebar/BuildMaterialsListSidebar';
import ClearMaterialsListControl from '../clearMaterialsListControl/ClearMaterialsListControl';
import MaterialsListMaterialControl from '../materialsListMaterialControl/MaterialsListMaterialControl';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import PopulateDealMaterialsListControl from '../populateDealMaterialsListControl/PopulateDealMaterialsListControl';
import './ManageableMaterialsList.sass';

type Props = {
  materialsListId: number;
  dealContextId: number;
};

const ManageableMaterialsList: FunctionComponent<Props> = ({
  materialsListId,
  dealContextId,
}: Props): JSX.Element | null => {
  const [
    showCreateMaterialsListMaterialControl,
    setShowCreateMaterialsListMaterialControl,
  ] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const deal = useDeal(dealContextId);

  const materialsListMaterials =
    useMaterialsListMaterialsForMaterialsList(materialsListId);

  const materialsList = useMaterialsList(materialsListId);

  const sortedMaterialsList = useMemo(() => {
    return materialsListMaterials.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [materialsListMaterials]);

  const getMaterialsList = useCallback(() => {
    dispatch(materialsListActions.requestGetMaterialsList(materialsListId));

    if (deal) {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.profitBreakdown(deal.publicId),
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'queryClient', and 'dispatch'
  }, [materialsListId, deal]);

  const getMaterialsListWithRelatedEntities = useCallback(() => {
    dispatch(
      materialsListActions.requestGetMaterialsListWithRelatedEntities(
        materialsListId
      )
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
  }, [materialsListId]);

  const renderMaterialsListMaterialControl = useCallback(
    (materialsListMaterialId: number) => {
      return (
        <MaterialsListMaterialControl
          materialsListMaterialId={materialsListMaterialId}
          onDeleteMaterialsListMaterialSuccess={getMaterialsList}
          onUpdateMaterialsListMaterialSuccess={getMaterialsList}
          showPreferredSupplierInfo={true}
        />
      );
    },
    [getMaterialsList]
  );

  const { mutateAsync: onAddMaterials } = useUpdateMaterialsListMaterials(
    deal?.publicId,
    {
      onSettled: () => {
        getMaterialsList();
        getMaterialsListWithRelatedEntities();
      },
    }
  );

  const disabledMaterialIds = useSelector((state) =>
    getMaterials(
      state,
      materialsListMaterials.map(
        (materialsListMaterial) => materialsListMaterial.materialId
      )
    )
  ).map((material) => material.publicId);

  if (!materialsList)
    return (
      <div className="manageable-materials-list-loader-container">
        <MiniLoader />
      </div>
    );

  return (
    <div className="manageable-materials-list">
      <PermissionGuard
        renderIfHasPermissions={[
          MaterialsListPermissions.PERSIST_MATERIALS_LIST_MATERIAL,
        ]}
      >
        <div className="actions">
          <Button
            onClick={() => setShowCreateMaterialsListMaterialControl(true)}
            className="mb-2"
            size={'sm'}
          >
            Search & add materials
          </Button>
          <PopulateDealMaterialsListControl
            className="flex-row-reverse justify-end"
            dealId={dealContextId}
          />
          <BuildMaterialsListSidebar
            isOpen={showCreateMaterialsListMaterialControl}
            onClose={() => setShowCreateMaterialsListMaterialControl(false)}
            disabledMaterialIds={disabledMaterialIds}
            onAddMaterials={async (materials) => {
              await onAddMaterials({
                materialsListId: materialsListId.toString(),
                materialsListMaterials: materials.map((material) => ({
                  materialId: material.id,
                  quantityChange: {
                    relative: material.quantity,
                  },
                })),
              });
              return;
            }}
          />
        </div>
      </PermissionGuard>
      {!!materialsListMaterials?.length && (
        <PermissionGuard
          renderIfHasPermissions={[
            MaterialsListPermissions.GET_MATERIALS_LIST_MATERIALS,
          ]}
        >
          <ul className="materials-list-materials">
            <li>
              <hr />
            </li>
            {sortedMaterialsList.map(
              (materialsListMaterial: MaterialsListMaterial, i: number) => {
                return (
                  <>
                    <li
                      key={`materials-list-material-control-${materialsListMaterial.id}`}
                    >
                      {renderMaterialsListMaterialControl(
                        materialsListMaterial.id
                      )}
                    </li>
                    <li
                      key={`materials-list-material-control-divider-${materialsListMaterial.id}`}
                    >
                      <hr />
                    </li>
                  </>
                );
              }
            )}
          </ul>
        </PermissionGuard>
      )}
      <div className="materials-list-management-actions">
        {!!materialsListMaterials?.length && (
          <ClearMaterialsListControl
            materialsListId={materialsListId}
            projectPublicId={deal?.publicId}
          />
        )}
      </div>
    </div>
  );
};

export default ManageableMaterialsList;
